/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 29th 2022, 10:28:59 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useDefaultProcessTemplateArtifact } from "common/hooks";
import { FormMany2OnePicker, FormInfoPopUpForm } from "form-builder/components";
import { TaskTemplate, FormConfiguration, FORM_STATUS_CHOICES_ENUM } from "lib/schemas";
import { useServiceBuilderContext } from "service-builder/providers";
import { TranslatableTextFormInput } from "translation/components";
import { SwitchFormInput } from "ui-widgets/forms";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { InputHelpDialogWrapper } from "ui-widgets";
import { TaskTemplateInput } from "./TaskTemplateInput";

export const TaskAssignmentSettings = () => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const { watch, resetField, getFieldState } = useOSFormContext<TaskTemplate>();

  const [watchedTaskTitle, watchedAssignTaskFlag, watchedAssignTitle] = watch<(keyof TaskTemplate)[]>([
    "title",
    "assign_task",
    "assign_title",
  ]);

  const assignTitleisDirty = getFieldState("assign_title").isDirty;

  React.useEffect(() => {
    if (watchedAssignTaskFlag && !watchedAssignTitle && !assignTitleisDirty)
      resetField("assign_title", { defaultValue: `Assign ${watchedTaskTitle}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAssignTaskFlag, watchedAssignTitle, assignTitleisDirty]);

  return (
    <>
      <InputHelpDialogWrapper
        helpTitle="Assign Task"
        helpContent="Select this if you want the task to be assigned before being worked on. Users with rights to assign this task will be able to assign it to other users. For client tasks, the user who initially submitted the process will be able to assign the task to other users via an email invitation"
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="assign_task"
          label="Task should be assigned before be worked on"
        />
      </InputHelpDialogWrapper>

      {watchedAssignTaskFlag && (
        <>
          <TranslatableTextFormInput<TaskTemplate>
            schema_field_key="assign_title"
            label="Assignment Button Text"
            helperText="The text to appear on the task assignment button. The default text will appear as 'Assign Task'"
          />

          <TaskAssignmentFormInput taskTitle={`${watchedTaskTitle}`} />

          <TaskTemplateInput<TaskTemplate>
            apiQueryParams={{ process_definition: currentProcessDefinition.id, type: "View" }}
            schema_field_key="default_reviewer_assign_task"
            label="Auto Assign the User that worked on below task"
          />

          <TaskTemplateInput<TaskTemplate>
            apiQueryParams={{ process_definition: currentProcessDefinition.id, type: "View" }}
            schema_field_key="default_reviewer_permit_task"
            label="Auto Permit User that worked on below task"
          />
        </>
      )}
    </>
  );
};

export const TaskAssignmentFormInput = ({ taskTitle }: { taskTitle: TaskTemplate["title"] }) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const defaultNewFormConfiguration: FormConfiguration = {
    ...useDefaultProcessTemplateArtifact(),
    form_type: "task_assignment_form",
    title: `${taskTitle} Assignment Form`,
    status: FORM_STATUS_CHOICES_ENUM.Published,
    login_required: true,
    sites: [currentProcessDefinition.site],
  };

  return (
    <FormMany2OnePicker<TaskTemplate>
      schema_field_key="assign_form"
      label="Task Assignment Form"
      apiQueryParams={{ form_type: "task_assignment_form" }}
      renderRelatedObjectForm={({ updatePrimaryObject, closeNewFormFn, newFormOpenState }) => (
        <FormInfoPopUpForm
          closeFormInfoPopUpFn={closeNewFormFn}
          formInfoPopUpOpenState={newFormOpenState}
          defaultNewFormConfiguration={defaultNewFormConfiguration}
          popUpTitle="New Task Assignment Form"
          onAPISaveFormInfoCallBackFn={(formConfiguration) => updatePrimaryObject(formConfiguration.id)}
        />
      )}
    />
  );
};
